import type { CartRoutes, ExpressCheckoutInfo } from '../../types';
import type { CartArticleType, CartSummary, CartAddresses } from 'services/types';
import { isNonEuropeanCountry } from 'utils';
import { isPaymentNeeded } from '../payment/isPaymentNeeded';
import { hasCartAddress } from './hasCartAddress';
import { matchesOriginCountry } from './matchesOriginCountry';
import { isPayPalExpressNewCustomerFlow, isPayPalExpressExistingCustomerFlow } from '../paypal-express';

/**
 * Computes express checkout route based on cart state
 *
 * Determines next checkout step URL and required actions based on:
 * - Cart articles and summary
 * - Shipping addresses (uses actual CartAddresses.addresses.shipping.country)
 * - User country
 * - PayPal Express parameters
 *
 * Priority order:
 * 1. Country-Address Mismatch (HIGHEST) → Clear address + route to /cart/address
 * 2. PayPal Express flows (new + existing) → Route to /cart/checkout + validate
 * 3. Has Address + (Has Payment OR Free Cart) → Route to /cart/checkout
 * 4. Has Address + Needs Payment → Route to /cart/payment
 * 5. Default (No Address) → Route to /cart/address
 *
 * @param data - Cart data (articles, summary, addresses)
 * @param config - Configuration (routes, user country, PayPal params)
 * @returns Express checkout information with routing and side effects
 */
export function computeExpressCheckoutRoute(
  data: {
    articles: CartArticleType[] | undefined;
    summary: CartSummary | undefined;
    addresses: CartAddresses | null | undefined;
  },
  config: {
    routes: CartRoutes;
    userCountry: string;
    ppExpressParam?: string;
  }
): ExpressCheckoutInfo {
  const { summary, addresses } = data;
  const { routes, userCountry, ppExpressParam } = config;

  // Compute metadata
  const hasAddress = hasCartAddress(addresses);
  const isPaymentRequired = isPaymentNeeded(summary);

  const hasPayment = Boolean(summary?.payment?.currentPayment);

  const currentPayment = summary?.payment?.currentPayment;

  // Determine address and user regionality — shipping takes precedence, fall back to order (mirrors Gatsby)
  const shippingAddress = addresses?.addresses?.shipping;
  const orderAddress = addresses?.addresses?.order;
  const shippingCountry = shippingAddress?.country || orderAddress?.country || '';
  const hasEuropeanAddress = hasAddress && !isNonEuropeanCountry(shippingCountry);
  const isEuropeanCountry = !isNonEuropeanCountry(userCountry);

  // Case-C (mirrors Gatsby): non-EU user with a non-EU address from a different non-EU country.
  // Currencies differ between non-EU countries so the address must be re-entered.
  const isAddressCountryMatchingCurrentCountry =
    matchesOriginCountry(shippingAddress?.country, userCountry) &&
    matchesOriginCountry(orderAddress?.country, userCountry);

  // Cases A & B: user and address are in different regions (EU vs. non-EU).
  const isRegionMismatch = isEuropeanCountry !== hasEuropeanAddress;
  // Case C: both non-EU, but the address is in another non-EU country.
  const isOtherNonEuropeanCountry =
    !isEuropeanCountry && !hasEuropeanAddress && !isAddressCountryMatchingCurrentCountry;
  const isCountryMismatch = hasAddress && (isRegionMismatch || isOtherNonEuropeanCountry);

  const baseMetadata = {
    hasAddress,
    hasPayment,
    isPaymentNeeded: isPaymentRequired,
    hasEuropeanAddress,
    isEuropeanCountry,
    isCountryMismatch,
    isAddressCountryMatchingCurrentCountry
  };

  // Priority 1: Country-Address Mismatch (HIGHEST PRIORITY)
  if (isCountryMismatch) {
    let reason: string;

    if (isEuropeanCountry && !hasEuropeanAddress) {
      reason = 'European user cannot use non-European address';
    } else if (!isEuropeanCountry && hasEuropeanAddress) {
      reason = 'Non-European user cannot use European address';
    } else {
      reason = 'Non-European user cannot use address from a different non-European country';
    }

    return {
      url: routes.address,
      shouldClearAddress: true,
      shouldValidatePayPalAddress: false,
      reason,
      metadata: baseMetadata,
      flow: 'normal'
    };
  }

  // Priority 2: PayPal Express New Customer
  if (isPayPalExpressNewCustomerFlow(ppExpressParam, currentPayment)) {
    return {
      url: routes.checkout,
      shouldClearAddress: false,
      shouldValidatePayPalAddress: true,
      reason: 'PayPal Express new customer checkout',
      metadata: baseMetadata,
      flow: 'paypal-new-customer'
    };
  }

  // Priority 3: PayPal Express Existing Customer
  if (isPayPalExpressExistingCustomerFlow(ppExpressParam, currentPayment)) {
    return {
      url: routes.checkout,
      shouldClearAddress: false,
      shouldValidatePayPalAddress: true,
      reason: 'PayPal Express existing customer checkout',
      metadata: baseMetadata,
      flow: 'paypal-existing-customer'
    };
  }

  // Priority 4: Normal Checkout Flow
  if (hasAddress) {
    // Has address + (has payment OR free cart) → go to checkout
    if (hasPayment || !isPaymentRequired) {
      const reason = hasPayment ? 'Has valid address and payment' : 'Has valid address, no payment needed';

      return {
        url: routes.checkout,
        shouldClearAddress: false,
        shouldValidatePayPalAddress: false,
        reason,
        metadata: baseMetadata,
        flow: 'normal'
      };
    }

    // Has address but needs payment → go to payment page
    return {
      url: routes.payment,
      shouldClearAddress: false,
      shouldValidatePayPalAddress: false,
      reason: 'Has valid address but needs payment',
      metadata: baseMetadata,
      flow: 'normal'
    };
  }

  // Priority 5: Default - No valid address
  return {
    url: routes.address,
    shouldClearAddress: false,
    shouldValidatePayPalAddress: false,
    reason: 'No valid address provided',
    metadata: baseMetadata,
    flow: 'normal'
  };
}
